import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { useRotateWebhookSecretMutation } from "@/pages/Private/redux/webhook/webhook.api";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { WebhookSecretResponse } from "../../schema/webhookSigning";

interface RotateWebhookSecretDialogProps {
	webhookId: number;
	onCancel: () => void;
	onRotated: (secret: WebhookSecretResponse) => void;
}

/**
 * Confirms a rotation (PREC-4249). The confirm runs once (web §24c). A refusal goes to the global
 * toast: a successful rotation refetches the table, and the dialog closes either way.
 */
export const RotateWebhookSecretDialog: FunctionComponent<RotateWebhookSecretDialogProps> = ({
	webhookId,
	onCancel,
	onRotated,
}) => {
	const { t } = useTranslation();
	const [rotateSecret] = useRotateWebhookSecretMutation();
	const { run, isRunning } = useSingleFlight();

	const handleRotate = () =>
		run(async () => {
			try {
				onRotated(await rotateSecret(webhookId).unwrap());
			} catch {
				showGlobalToast(t("webhookSigning.rotate.failed"));
				onCancel();
			}
		});

	return (
		<Modal
			handleClose={isRunning ? undefined : onCancel}
			handleSave={handleRotate}
			isLoading={isRunning}
			isOpened={true}
			submitButtonColor={ButtonColor.ACTION}
			submitButtonText={t("webhookSigning.rotate.confirm")}
			title={t("webhookSigning.rotate.title")}
		>
			<div className="flex flex-col gap-2 text-sm text-text" data-test="webhook-rotate-dialog">
				<p>{t("webhookSigning.rotate.body")}</p>
				<p className="text-xs text-text-meta">{t("webhookSigning.rotate.again")}</p>
			</div>
		</Modal>
	);
};
